import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import React from "react";

interface Stats {
    date: Date;
    value: number;
}

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface YearOverYearChartProps {
    stats: Stats[];
    valueFormatter: (value: number) => string;
    startFromMonth?: number;
}

const transformToYearOverYearData = (stats: Stats[]): { [month: string]: { [year: string]: number } } => {
    const result: { [month: string]: { [year: string]: number } } = {};

    monthNames.forEach((month) => {
        result[month] = {};
    });

    stats.forEach((stat) => {
        const date = new Date(stat.date);
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear().toString();

        if (!result[month]) {
            result[month] = {};
        }

        result[month][year] = stat.value;
    });

    return result;
};

function orderMonths(startFromMonth: number) {
    const orderedMonths: string[] = [];
    let i = 0;
    while (orderedMonths.length < 12) {
        if (orderedMonths.length === 12) {
            break;
        }
        orderedMonths.push(monthNames[(startFromMonth + i) % 12]);
        i++;
    }
    return orderedMonths;
}

function extractYears(stats: Stats[]): number[] {
    const years = new Set<number>();
    stats.forEach((stat) => {
        if (stat.value !== 0) {
            const date = new Date(stat.date);
            years.add(date.getFullYear());
        }
    });
    return Array.from(years).sort((a, b) => a - b);
}

const YearOverYearChart: React.FC<YearOverYearChartProps> = ({ stats, valueFormatter, startFromMonth = 0 }) => {
    const yearColors = {
        current: "#1e40af", // dark blue
        previous: "#64748b", // slate-500
        twoPrior: "#cbd5e1", // slate-300
    };

    const years = extractYears(stats);

    const transformedData = transformToYearOverYearData(stats);
    const orderedMonths = orderMonths(startFromMonth);
    const chartData = orderedMonths.map((month) => ({
        month,
        ...transformedData[month],
    }));

    return (
        <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 32 }} // Increased bottom margin for legend
                >
                    <XAxis dataKey="month" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} tickFormatter={valueFormatter} />
                    <Tooltip
                        formatter={(value: number | undefined, name: string | undefined) => [value !== undefined ? valueFormatter(value) : "", `${name ?? ""}`]}
                        contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            padding: "8px",
                        }}
                        cursor={{ fill: "#f8fafc" }}
                    />
                    <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{
                            paddingTop: "8px",
                            fontSize: "12px",
                            color: "#888888",
                        }}
                    />
                    {years.map((year, index) => (
                        <Bar
                            key={year}
                            dataKey={year.toString()}
                            name={year.toString()}
                            fill={index === 0 ? yearColors.twoPrior : index === 1 ? yearColors.previous : yearColors.current}
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default YearOverYearChart;
